
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { TeamMember, TeamRole } from "@/integrations/supabase/types/";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserPlus, Pencil, Trash2 } from "lucide-react";
import { InvitationManagement } from "./InvitationManagement";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const TeamMemberManagement = () => {
  const { toast } = useToast();
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isEditingMember, setIsEditingMember] = useState(false);
  const [isDeletingMember, setIsDeletingMember] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [newMember, setNewMember] = useState<{
    first_name: string;
    last_name: string;
    email: string;
    role: TeamRole;
    department: string;
    permissions: Record<string, any>;
  }>({
    first_name: "",
    last_name: "",
    email: "",
    role: "member",
    department: "",
    permissions: {},
  });

  const [activeTab, setActiveTab] = useState("members");

  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUserId(session.user.id);
      }
    };
    getUser();
  }, []);

  const { data: members, refetch } = useQuery({
    queryKey: ["team-members"],
    queryFn: async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase
        .from("team_members")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        toast({
          title: "Error",
          description: "Failed to load team members",
          variant: "destructive",
        });
        throw error;
      }
      return data as TeamMember[];
    },
    enabled: !!userId,
  });

  const handleAddMember = async () => {
    if (!userId) {
      toast({
        title: "Error",
        description: "You must be logged in to add team members",
        variant: "destructive",
      });
      return;
    }

    try {
      // First check if email already exists
      const { data: existingMember } = await supabase
        .from("team_members")
        .select("email")
        .eq("email", newMember.email)
        .single();

      if (existingMember) {
        toast({
          title: "Error",
          description: "A team member with this email already exists",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase.from("team_members").insert([{
        ...newMember,
        user_id: userId,
        owner_id: userId
      }]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Team member added successfully",
      });

      setIsAddingMember(false);
      setNewMember({
        first_name: "",
        last_name: "",
        email: "",
        role: "member",
        department: "",
        permissions: {},
      });
      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to add team member",
        variant: "destructive",
      });
    }
  };

  const handleEditMember = async () => {
    if (!selectedMember) return;

    try {
      const { error } = await supabase
        .from("team_members")
        .update({
          first_name: selectedMember.first_name,
          last_name: selectedMember.last_name,
          email: selectedMember.email,
          role: selectedMember.role,
          department: selectedMember.department,
        })
        .eq("id", selectedMember.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Team member updated successfully",
      });

      setIsEditingMember(false);
      setSelectedMember(null);
      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update team member",
        variant: "destructive",
      });
    }
  };

  const handleDeleteMember = async () => {
    if (!selectedMember) return;

    try {
      const { error } = await supabase
        .from("team_members")
        .delete()
        .eq("id", selectedMember.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Team member deleted successfully",
      });

      setIsDeletingMember(false);
      setSelectedMember(null);
      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete team member",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      <Tabs defaultValue="members" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="members">Team Members</TabsTrigger>
          <TabsTrigger value="invitations">Invitations</TabsTrigger>
        </TabsList>
        
        <TabsContent value="members" className="pt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">Team Members</h2>
            <Dialog open={isAddingMember} onOpenChange={setIsAddingMember}>
              <DialogTrigger asChild>
                <Button>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Member
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Team Member</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name</Label>
                      <Input
                        id="first_name"
                        value={newMember.first_name}
                        onChange={(e) =>
                          setNewMember({ ...newMember, first_name: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name</Label>
                      <Input
                        id="last_name"
                        value={newMember.last_name}
                        onChange={(e) =>
                          setNewMember({ ...newMember, last_name: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newMember.email}
                      onChange={(e) =>
                        setNewMember({ ...newMember, email: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select
                      value={newMember.role}
                      onValueChange={(value: TeamRole) =>
                        setNewMember({ ...newMember, role: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="department">Department</Label>
                    <Input
                      id="department"
                      value={newMember.department}
                      onChange={(e) =>
                        setNewMember({ ...newMember, department: e.target.value })
                      }
                    />
                  </div>
                  <Button className="w-full" onClick={handleAddMember}>
                    Add Team Member
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {members?.map((member) => (
              <Card key={member.id}>
                <CardContent className="p-6">
                  <div className="space-y-2">
                    <h3 className="font-medium">
                      {member.first_name} {member.last_name}
                    </h3>
                    <p className="text-sm text-muted-foreground">{member.email}</p>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                        {member.role}
                      </span>
                      {member.department && (
                        <span className="text-xs bg-muted px-2 py-1 rounded">
                          {member.department}
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="px-6 py-4 bg-muted/50 flex justify-end space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedMember(member);
                      setIsEditingMember(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedMember(member);
                      setIsDeletingMember(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </TabsContent>
        
        <TabsContent value="invitations" className="pt-4">
          <InvitationManagement />
        </TabsContent>
      </Tabs>

      {/* Edit Member Dialog */}
      <Dialog open={isEditingMember} onOpenChange={setIsEditingMember}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_first_name">First Name</Label>
                <Input
                  id="edit_first_name"
                  value={selectedMember?.first_name || ""}
                  onChange={(e) =>
                    setSelectedMember(
                      selectedMember
                        ? { ...selectedMember, first_name: e.target.value }
                        : null
                    )
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_last_name">Last Name</Label>
                <Input
                  id="edit_last_name"
                  value={selectedMember?.last_name || ""}
                  onChange={(e) =>
                    setSelectedMember(
                      selectedMember
                        ? { ...selectedMember, last_name: e.target.value }
                        : null
                    )
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                type="email"
                value={selectedMember?.email || ""}
                onChange={(e) =>
                  setSelectedMember(
                    selectedMember
                      ? { ...selectedMember, email: e.target.value }
                      : null
                  )
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_role">Role</Label>
              <Select
                value={selectedMember?.role || "member"}
                onValueChange={(value: TeamRole) =>
                  setSelectedMember(
                    selectedMember
                      ? { ...selectedMember, role: value }
                      : null
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_department">Department</Label>
              <Input
                id="edit_department"
                value={selectedMember?.department || ""}
                onChange={(e) =>
                  setSelectedMember(
                    selectedMember
                      ? { ...selectedMember, department: e.target.value }
                      : null
                  )
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditingMember(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditMember}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeletingMember} onOpenChange={setIsDeletingMember}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the team
              member and remove them from the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeletingMember(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteMember}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
